import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Loader2, Wand2, AlertTriangle } from 'lucide-react';
import { YearbookPage, YearbookElement, PageBackground } from '@/types/yearbook';
import { ApiTemplateObject, parseTemplateObjects } from '@/lib/parseTemplateObjects';
import { editorUrl } from '@/lib/editorBasePath';

interface AlbumMagiquePageData {
  page_index: number;
  trame_template_id?: number;
  objects_position: ApiTemplateObject[];
  background_image_url?: string;
  trame_background_id?: number;
}

interface AlbumMagiqueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pages: YearbookPage[];
  onApplyMagicPage: (
    pageIndex: number,
    elements: YearbookElement[],
    background?: PageBackground,
    layoutTemplateId?: number
  ) => void;
  onNavigateToPage: (pageIndex: number) => void;
}

const STYLES = [
  { value: 'large', label: 'Privilégier de grandes photos' },
  { value: 'mix', label: 'Un mix de grandes et de petites photos' },
  { value: 'maximum', label: 'Le plus de photos possible' },
];

const generateId = () => Math.random().toString(36).substring(2, 11);

const AlbumMagiqueDialog: React.FC<AlbumMagiqueDialogProps> = ({
  open,
  onOpenChange,
  pages,
  onApplyMagicPage,
  onNavigateToPage,
}) => {
  const [style, setStyle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleReset = useCallback(() => {
    setStyle('');
    setLoading(false);
    setError('');
  }, []);

  const handleOpenChange = useCallback(
    (v: boolean) => {
      if (!v) handleReset();
      onOpenChange(v);
    },
    [onOpenChange, handleReset]
  );

  const handleGenerate = useCallback(async () => {
    if (!style) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch(editorUrl('/get_album_magique'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ style }),
      });

      if (!res.ok) throw new Error(`Erreur ${res.status}`);

      const data: { pages: AlbumMagiquePageData[] } = await res.json();

      if (!data.pages?.length) {
        setError('Aucune page générée. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      let firstPageIndex: number | null = null;

      for (const pageData of data.pages) {
        const { elements, background: parsedBg } = parseTemplateObjects(
          pageData.objects_position,
          generateId
        );

        const background: PageBackground | undefined = pageData.background_image_url
          ? {
              type: 'image',
              value: pageData.background_image_url,
              trame_background_id: pageData.trame_background_id ?? null,
            }
          : parsedBg;

        onApplyMagicPage(
          pageData.page_index,
          elements,
          background,
          pageData.trame_template_id
        );

        if (firstPageIndex === null) firstPageIndex = pageData.page_index;
      }

      if (firstPageIndex !== null) onNavigateToPage(firstPageIndex);
      onOpenChange(false);
    } catch (err: any) {
      console.error('[AlbumMagique] Error:', err);
      setError(err?.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  }, [style, onApplyMagicPage, onNavigateToPage, onOpenChange]);

  const isValid = !!style;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 size={18} className="text-primary" />
            Album Magique
          </DialogTitle>
          <DialogDescription>
            Configurez votre album et laissez la magie opérer !
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Style */}
          <div className="space-y-2.5">
            <p className="text-sm font-medium">Choisissez un style de remplissage</p>
            <RadioGroup value={style} onValueChange={setStyle} className="grid gap-2">
              {STYLES.map((s) => (
                <div key={s.value} className="flex items-center gap-2.5">
                  <RadioGroupItem value={s.value} id={`style-${s.value}`} />
                  <Label htmlFor={`style-${s.value}`} className="cursor-pointer text-sm">
                    {s.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {error && (
            <p className="text-xs text-destructive text-center">{error}</p>
          )}

          {/* Generate button */}
          <div className="space-y-2">
            <button
              onClick={handleGenerate}
              disabled={!isValid || loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Génération en cours…
                </>
              ) : (
                <>
                  <Wand2 size={16} />
                  Générer l'album
                </>
              )}
            </button>
            <div className="flex items-start gap-1.5 justify-center">
              <AlertTriangle size={12} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[11px] text-muted-foreground text-center leading-snug">
                La création de l'album remplacera la mise en page actuelle
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AlbumMagiqueDialog;
